import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, CheckCircle, Clock, FileCheck, Search, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { publicApi } from '../lib/api';

const TrademarkServicesPage = () => {
  useEffect(() => {
    publicApi.trackEvent({ event_type: 'page_view', page: 'trademark-services' }).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  const whyHireAttorney = [
    "Determine whether your name or logo can be registered",
    "Avoid conflicts with existing trademarks",
    "Select the correct filing class",
    "Prepare a complete USPTO application",
    "Respond to Office Actions if the USPTO raises issues",
    "Protect your brand as your business grows"
  ];

  const whoNeedsRegistration = [
    "Small business owners",
    "Startups and entrepreneurs",
    "Consultants and service providers",
    "E-commerce brands",
    "Content creators",
    "Product companies",
    "Existing businesses expanding into new markets"
  ];

  const filingProcess = [
    { step: 1, title: "Schedule a consultation", desc: "Meet with a trademark attorney to discuss your needs" },
    { step: 2, title: "Trademark search", desc: "Perform trademark search and risk review" },
    { step: 3, title: "Prepare and file", desc: "Prepare and file USPTO application" },
    { step: 4, title: "USPTO review", desc: "Respond to USPTO questions if needed" },
    { step: 5, title: "Monitor registration", desc: "Monitor registration process through completion" }
  ];

  const pricingPackages = [
    {
      name: "Trademark Starter Package",
      price: "$1,200 – $1,500",
      note: "+ USPTO filing fees",
      description: "Best for business owners ready to file a federal trademark application.",
      features: [
        "Trademark search and availability review",
        "Strategy consultation with a trademark attorney",
        "Identification of appropriate class",
        "Preparation and filing of one USPTO trademark application",
        "Specimen review",
        "Monitoring through initial USPTO review"
      ],
      extras: "Additional class: $250 – $350. USPTO filing fees are not included.",
      featured: true
    },
    {
      name: "Trademark + Business Formation Bundle",
      price: "$2,500 – $3,500",
      note: "+ filing fees",
      description: "Best for new businesses that need both trademark protection and legal structure.",
      features: [
        "Trademark search and filing",
        "Business formation consultation",
        "LLC or corporation formation guidance (Texas or Georgia)",
        "Basic operating agreement template"
      ],
      extras: "Recommended for entrepreneurs who want to protect their brand and business at the same time."
    },
    {
      name: "Trademark Search Consultation",
      price: "$300 – $450",
      description: "For clients who want to check availability before filing.",
      features: [
        "Trademark search",
        "Risk analysis",
        "Filing strategy discussion",
        "Recommendations for next steps"
      ],
      extras: "Consultation fee is credited toward filing if retained within 7 days."
    },
    {
      name: "Office Action Response & Support",
      price: "$400 – $1,500",
      note: "depending on complexity",
      description: "If the USPTO issues a refusal or request for clarification.",
      features: [
        "Review of USPTO Office Action",
        "Preparation of response",
        "Amendment of application where appropriate",
        "Filing of response with the USPTO"
      ],
      extras: "Not all refusals can be overcome. A consultation is required before quoting a fee."
    }
  ];

  return (
    <div data-testid="trademark-services-page">
      {/* Hero Section */}
      <section className="bg-[#FDFCFA] border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-8 h-8 text-[#1F2A44]" />
              <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D]">
                Trademark Attorney Services
              </p>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif tracking-tight leading-[1.1] text-[#222222] mb-6">
              Trademark Filing and Brand Protection Services
            </h1>
            <p className="text-lg md:text-xl text-[#555555] leading-relaxed mb-8">
              Your business's name, logo, and brand identity are valuable assets. Registering a trademark correctly helps protect your brand, avoid conflicts, and prevent costly legal problems later. J Brantley Law provides trademark attorney services for clients in Texas, Georgia, and nationwide who need guidance with trademark search, trademark filing, and trademark registration through the United States Patent and Trademark Office (USPTO).
            </p>
            <p className="text-lg text-[#555555] leading-relaxed mb-10">
              Whether you're starting a new business, launching a product, or protecting an existing brand, working with a trademark lawyer helps ensure your application is prepared correctly the first time.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/book" data-testid="trademark-cta-book">
                <Button className="bg-[#1F2A44] hover:bg-[#151D30] text-white px-10 py-4 rounded-sm text-sm font-medium tracking-wide uppercase">
                  Schedule Consultation
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button variant="outline" className="border-[#1F2A44] text-[#1F2A44] hover:bg-[#1F2A44] hover:text-white px-10 py-4 rounded-sm text-sm font-medium tracking-wide uppercase">
                  View All Pricing
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Hire a Trademark Attorney */}
      <section className="bg-white border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <div>
              <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D] mb-4">
                Why It Matters
              </p>
              <h2 className="text-3xl md:text-4xl font-serif text-[#222222] mb-6">
                Why Hire a Trademark Attorney Instead of Filing Yourself
              </h2>
              <p className="text-[#555555] leading-relaxed mb-6">
                Many online services allow you to submit a trademark application, but they cannot provide legal advice about whether your mark is available or likely to be approved.
              </p>
              <p className="text-[#555555] leading-relaxed">
                Filing a trademark without legal review can lead to delays, refusals, or the need to start over.
              </p>
            </div>
            <div className="bg-[#F5F3EF] p-8 md:p-10">
              <h3 className="text-lg font-serif text-[#222222] mb-6">Working with a trademark attorney can help you:</h3>
              <ul className="space-y-4">
                {whyHireAttorney.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#1F2A44] mt-0.5 flex-shrink-0" />
                    <span className="text-[#555555]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="bg-[#F5F3EF] border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="text-center mb-12">
            <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D] mb-4">
              Transparent Pricing
            </p>
            <h2 className="text-3xl md:text-4xl font-serif text-[#222222] mb-4">
              Trademark Service Packages
            </h2>
            <p className="text-[#555555] max-w-2xl mx-auto">
              Clear, upfront pricing so you know what to expect before we begin.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {pricingPackages.map((pkg, index) => (
              <div 
                key={index} 
                className={`bg-white p-8 border ${pkg.featured ? 'border-[#C2A46D] ring-2 ring-[#C2A46D]/20' : 'border-[#E8E4DC]'}`}
                data-testid={`pricing-package-${index}`}
              >
                {pkg.featured && (
                  <span className="inline-block bg-[#C2A46D] text-[#1F2A44] text-xs font-medium px-3 py-1 mb-4 uppercase tracking-wider">
                    Most Popular
                  </span>
                )}
                <h3 className="text-xl font-serif text-[#222222] mb-2">{pkg.name}</h3>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-2xl md:text-3xl font-serif text-[#1F2A44]">{pkg.price}</span>
                  {pkg.note && <span className="text-sm text-[#555555]">{pkg.note}</span>}
                </div>
                <p className="text-[#555555] text-sm mb-6">{pkg.description}</p>
                <ul className="space-y-3 mb-6">
                  {pkg.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-[#1F2A44] mt-0.5 flex-shrink-0" />
                      <span className="text-[#555555]">{feature}</span>
                    </li>
                  ))}
                </ul>
                {pkg.extras && (
                  <p className="text-xs text-[#777777] border-t border-[#E8E4DC] pt-4">
                    {pkg.extras}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Needs Registration */}
      <section className="bg-white border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D] mb-4">
              Is This Right For You?
            </p>
            <h2 className="text-3xl md:text-4xl font-serif text-[#222222] mb-6">
              Who Needs Trademark Registration
            </h2>
            <p className="text-[#555555]">
              Trademark services may be appropriate for:
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {whoNeedsRegistration.map((item, index) => (
              <div 
                key={index} 
                className="bg-[#F5F3EF] p-4 text-center border border-[#E8E4DC]"
              >
                <span className="text-sm text-[#555555]">{item}</span>
              </div>
            ))}
          </div>

          <p className="text-center text-[#555555] mt-8">
            If you are not sure whether you need a trademark, a consultation is the best place to start.
          </p>
        </div>
      </section>

      {/* Filing Process */}
      <section className="bg-[#1F2A44] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="text-center mb-12">
            <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D] mb-4">
              The Process
            </p>
            <h2 className="text-3xl md:text-4xl font-serif mb-4">
              Trademark Filing Process
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              Most trademark applications take several months to complete. As of January 2026, the average time from filing to trademark registration is approximately 10.2 months.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {filingProcess.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 bg-[#C2A46D] text-[#1F2A44] rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-serif font-bold">
                  {step.step}
                </div>
                <h3 className="font-medium mb-2">{step.title}</h3>
                <p className="text-sm text-white/60">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#FDFCFA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="max-w-3xl mx-auto text-center">
            <Shield className="w-12 h-12 text-[#C2A46D] mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-serif text-[#222222] mb-6">
              Schedule a Trademark Consultation
            </h2>
            <p className="text-lg text-[#555555] mb-4">
              All trademark matters begin with a consultation so the scope of work can be evaluated before filing. Schedule a consultation to discuss your trademark, brand name, or logo and determine the best filing strategy.
            </p>
            <p className="text-sm text-[#777777] mb-8">
              Submitting an inquiry does not create an attorney-client relationship.
            </p>
            <Link to="/book" data-testid="trademark-cta-bottom">
              <Button className="bg-[#C2A46D] hover:bg-[#B39660] text-[#1F2A44] px-12 py-4 rounded-sm text-sm font-semibold tracking-wide uppercase">
                Schedule Consultation
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer Note */}
      <section className="bg-[#F5F3EF] border-t border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
          <p className="text-center text-sm text-[#555555]">
            <strong>J Brantley Law, PLLC</strong> · Trademark Attorney Services for Georgia and Texas Clients · Licensed in Georgia and Texas
          </p>
        </div>
      </section>
    </div>
  );
};

export default TrademarkServicesPage;
